import { Plus } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ComboItem {
  id: string;
  label: string;
  /** Extra text matched by the filter (number, type). */
  keywords?: string;
  group?: string;
  hint?: ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  indent?: number;
}

/**
 * Select / Combobox (document 05): type to filter, arrows to move, Enter or Tab to pick,
 * grouped options, `+ Add new` as the first option. The input shows the chosen label.
 */
export function Combobox({
  items,
  value,
  onChange,
  placeholder,
  ariaLabel,
  invalid,
  onAddNew,
  addNewLabel,
  className,
  inputClassName,
}: {
  items: ComboItem[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  ariaLabel: string;
  invalid?: boolean;
  onAddNew?: (typed: string) => void;
  addNewLabel?: string;
  className?: string;
  inputClassName?: string;
}) {
  const t = useT();
  const selected = items.find((i) => i.id === value) ?? null;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(selected?.label ?? '');
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setText(selected?.label ?? '');
  }, [selected?.label, open]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f
      ? items.filter((i) => `${i.label} ${i.keywords ?? ''}`.toLowerCase().includes(f))
      : items;
  }, [items, filter]);
  // option 0 is "+ Add new" when offered
  const offset = onAddNew ? 1 : 0;
  const count = shown.length + offset;

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (index: number) => {
    if (onAddNew && index === 0) {
      setOpen(false);
      onAddNew(filter);
      return;
    }
    const item = shown[index - offset];
    if (!item || item.disabled) return;
    onChange(item.id);
    setText(item.label);
    setOpen(false);
  };

  const openList = () => {
    setFilter('');
    const idx = selected ? shown.findIndex((i) => i.id === selected.id) : -1;
    setActive(idx >= 0 ? idx + offset : offset);
    setOpen(true);
  };

  let lastGroup: string | undefined;
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <div className={cn('relative', className)}>
          <input
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={invalid || undefined}
            autoComplete="off"
            placeholder={placeholder}
            value={text}
            className={cn(
              'h-9 w-full rounded-md border border-input bg-card px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive',
              inputClassName,
            )}
            onFocus={(e) => {
              e.target.select();
            }}
            onClick={() => !open && openList()}
            onChange={(e) => {
              setText(e.target.value);
              setFilter(e.target.value);
              setActive(offset);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!open) return openList();
                setActive((a) => Math.min(count - 1, a + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(0, a - 1));
              } else if (e.key === 'Enter' && open) {
                e.preventDefault();
                pick(active);
              } else if (e.key === 'Tab' && open && filter) {
                pick(active === 0 && onAddNew ? offset : active);
              } else if (e.key === 'Escape' && open) {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                setText(selected?.label ?? '');
              }
            }}
            onBlur={() => {
              if (text.trim() === '' && value) onChange(null);
              else setText(selected?.label ?? '');
              setOpen(false);
            }}
          />
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if ((e.target as HTMLElement | null)?.closest?.('[role=combobox]')) e.preventDefault();
          }}
          className="z-50 max-h-80 w-[var(--radix-popover-trigger-width)] min-w-[320px] overflow-y-auto rounded-md border bg-popover p-1 text-sm shadow-[0_4px_16px_rgba(17,24,39,0.12)]"
        >
          <div ref={listRef} id={listId} role="listbox" aria-label={ariaLabel}>
            {onAddNew ? (
              <div
                role="option"
                aria-selected={active === 0}
                data-index={0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(0)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-medium text-primary',
                  active === 0 && 'bg-accent',
                )}
              >
                <Plus className="size-4" /> {addNewLabel ?? t('ui.addNew')}
              </div>
            ) : null}
            {shown.length === 0 ? (
              <div className="px-2 py-1.5 text-muted-foreground">{t('ui.noMatches')}</div>
            ) : null}
            {shown.map((item, i) => {
              const index = i + offset;
              const header = item.group && item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <div key={item.id}>
                  {header ? (
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {header}
                    </div>
                  ) : null}
                  <div
                    role="option"
                    aria-selected={active === index}
                    aria-disabled={item.disabled || undefined}
                    data-index={index}
                    title={item.disabled ? item.disabledReason : undefined}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(index)}
                    style={{ paddingInlineStart: 8 + (item.indent ?? 0) * 14 }}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-3 rounded py-1.5 pe-2',
                      active === index && 'bg-accent text-accent-foreground',
                      item.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.hint ? (
                      <span className="shrink-0 text-xs text-muted-foreground">{item.hint}</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
